import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Eye, Pencil, Plus, Power, PowerOff, Search, Trash2, Video } from 'lucide-react'
import { PageContainer, PageHeader } from '../../components/Layout'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ConfirmModal,
  DataTable,
  EmptyState,
  Input,
  Pagination,
  Spinner,
} from '../../components/UI'
import { entrepreneurCatalogService } from '../../services/entrepreneurCatalogService'
import ProductFormModal from './components/ProductFormModal'
import ProductViewModal from './components/ProductViewModal'

const pageSize = 10

const emptyProductForm = {
  categoryId: '',
  name: '',
  slug: '',
  description: '',
  presentation: '',
  price: 0,
  sortOrder: 0,
  isActive: true,
  videos: [],
}

const emptyVideoForm = {
  videoUrl: '',
}

const emptyModalState = {
  product: emptyProductForm,
  video: emptyVideoForm,
}

const cloneProduct = (product) => ({
  id: product?.id,
  categoryId: product?.categoryId ?? null,
  name: product?.name || '',
  slug: product?.slug || '',
  description: product?.description || '',
  presentation: product?.presentation || '',
  price: Number(product?.price || 0),
  sortOrder: product?.sortOrder ?? 0,
  isActive: product?.isActive ?? true,
  category: product?.category || null,
  videos: Array.isArray(product?.videos) ? product.videos : [],
})

const readProductItems = (response) => {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.items)) return response.items
  if (Array.isArray(response?.data?.items)) return response.data.items
  if (Array.isArray(response?.data)) return response.data
  return []
}

const readPagination = (response, fallbackPage) => {
  if (response && typeof response === 'object' && !Array.isArray(response)) {
    return {
      page: Number(response.page ?? response.currentPage ?? fallbackPage ?? 1),
      pageSize: Number(response.pageSize ?? response.limit ?? pageSize),
      totalCount: Number(response.totalCount ?? response.totalItems ?? readProductItems(response).length),
      totalPages: Number(response.totalPages ?? response.pages ?? 1),
    }
  }

  return {
    page: fallbackPage ?? 1,
    pageSize,
    totalCount: readProductItems(response).length,
    totalPages: 1,
  }
}

export default function EntrepreneurCatalogProducts() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [profile, setProfile] = useState(null)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize,
    totalCount: 0,
    totalPages: 1,
  })
  const [searchQuery, setSearchQuery] = useState('')
  const [appliedQuery, setAppliedQuery] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [createModal, setCreateModal] = useState(emptyModalState)
  const [editOpen, setEditOpen] = useState(false)
  const [editModal, setEditModal] = useState(emptyModalState)
  const [viewProduct, setViewProduct] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  const loadData = useCallback(async (requestedPage = 1, query = '') => {
    setLoading(true)
    setMessage('')
    try {
      const [profileResult, categoriesResult, productsResult] = await Promise.allSettled([
        entrepreneurCatalogService.getProfile(),
        entrepreneurCatalogService.getCategories(),
        entrepreneurCatalogService.getProducts({
          q: query,
          page: requestedPage,
          pageSize,
        }),
      ])

      if (profileResult.status === 'fulfilled') {
        setProfile(profileResult.value)
      }

      if (categoriesResult.status === 'fulfilled') {
        setCategories(Array.isArray(categoriesResult.value) ? categoriesResult.value : [])
      }

      if (productsResult.status === 'fulfilled') {
        const items = readProductItems(productsResult.value)
        const paging = readPagination(productsResult.value, requestedPage)
        setProducts(items)
        setPagination(paging)
      } else {
        throw productsResult.reason
      }
    } catch (error) {
      if (error?.response?.status === 402) {
        setMessage('Este modulo requiere licencia activa.')
      } else {
        setMessage(error?.message || 'No fue posible cargar los productos.')
      }
      setProducts([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData(pagination.page, appliedQuery)
  }, [appliedQuery, pagination.page, loadData])

  const handleSearchSubmit = (event) => {
    event.preventDefault()
    const nextQuery = searchQuery.trim()

    setAppliedQuery(nextQuery)
    setPagination((current) => (current.page === 1 ? current : { ...current, page: 1 }))
  }

  const totalResults = pagination.totalCount || products.length

  const categoryOptions = useMemo(() => categories.map((category) => ({
    value: String(category.id),
    label: category.name,
  })), [categories])

  const updateCreateProductField = (field, value) => {
    setCreateModal((current) => ({
      ...current,
      product: { ...current.product, [field]: value },
    }))
  }

  const updateCreateVideoField = (field, value) => {
    setCreateModal((current) => ({
      ...current,
      video: { ...current.video, [field]: value },
    }))
  }

  const updateEditProductField = (field, value) => {
    setEditModal((current) => ({
      ...current,
      product: { ...current.product, [field]: value },
    }))
  }

  const updateEditVideoField = (field, value) => {
    setEditModal((current) => ({
      ...current,
      video: { ...current.video, [field]: value },
    }))
  }

  const persistProduct = async (product, successMessage) => {
    await entrepreneurCatalogService.updateProduct(product.id, {
      id: product.id,
      categoryId: product.categoryId || null,
      name: product.name || '',
      slug: product.slug || '',
      description: product.description || '',
      presentation: product.presentation || '',
      price: Number(product.price || 0),
      sortOrder: Number(product.sortOrder || 0),
      isActive: Boolean(product.isActive),
    })

    setMessage(successMessage || `Producto "${product.name}" guardado.`)
  }

  const saveExistingProduct = async (product) => {
    setSaving(true)
    setMessage('')
    try {
      await persistProduct(product)
      await loadData(pagination.page, appliedQuery)
    } catch (error) {
      setMessage(error?.message || 'No fue posible guardar el producto.')
    } finally {
      setSaving(false)
    }
  }

  const createProduct = async () => {
    setSaving(true)
    setMessage('')
    try {
      const createdProduct = await entrepreneurCatalogService.createProduct({
        ...createModal.product,
        categoryId: createModal.product.categoryId ? Number(createModal.product.categoryId) : null,
        price: Number(createModal.product.price || 0),
        sortOrder: Number(createModal.product.sortOrder || 0),
      })

      const createdId = createdProduct?.id || createdProduct?.data?.id || createdProduct?.product?.id
      const hasVideo = Boolean(createModal.video.videoUrl)

      if (createdId && hasVideo) {
        await entrepreneurCatalogService.createVideo(createdId, {
          videoUrl: createModal.video.videoUrl,
        })
      }

      setCreateModal(emptyModalState)
      setCreateOpen(false)
      setMessage('Producto creado correctamente.')
      await loadData(pagination.page, appliedQuery)
    } catch (error) {
      setMessage(error?.message || 'No fue posible crear el producto.')
    } finally {
      setSaving(false)
    }
  }

  const saveEditProduct = async () => {
    const payload = editModal.product

    setSaving(true)
    setMessage('')
    try {
      await persistProduct(payload, `Producto "${payload.name}" guardado.`)

      const hasVideo = Boolean(editModal.video.videoUrl)
      if (hasVideo && payload.id) {
        await entrepreneurCatalogService.createVideo(payload.id, {
          videoUrl: editModal.video.videoUrl,
        })
      }

      setEditModal(emptyModalState)
      setEditOpen(false)
      await loadData(pagination.page, appliedQuery)
    } catch (error) {
      setMessage(error?.message || 'No fue posible guardar el producto.')
    } finally {
      setSaving(false)
    }
  }

  const removeProduct = async (productId) => {
    setSaving(true)
    setMessage('')
    try {
      await entrepreneurCatalogService.deleteProduct(productId)
      setMessage('Producto eliminado.')
      await loadData(pagination.page, appliedQuery)
    } catch (error) {
      setMessage(error?.message || 'No fue posible eliminar el producto.')
    } finally {
      setSaving(false)
    }
  }

  const deleteVideo = async (productId, videoId) => {
    setSaving(true)
    setMessage('')
    try {
      await entrepreneurCatalogService.deleteVideo(productId, videoId)
      setMessage('Video eliminado.')
      const refreshedProducts = await entrepreneurCatalogService.getProducts({
        q: appliedQuery,
        page: pagination.page,
        pageSize,
      })
      const refreshedItems = readProductItems(refreshedProducts)
      setProducts(refreshedItems)
      const refreshedProduct = refreshedItems.find((item) => item.id === productId)

      if (refreshedProduct) {
        setEditModal((current) => ({
          ...current,
          product: cloneProduct(refreshedProduct),
        }))
      }
    } catch (error) {
      setMessage(error?.message || 'No fue posible eliminar el video.')
    } finally {
      setSaving(false)
    }
  }

  const openCreateModal = () => {
    setCreateModal(emptyModalState)
    setCreateOpen(true)
  }

  const openEditModal = (product) => {
    setEditModal({
      product: cloneProduct(product),
      video: emptyVideoForm,
    })
    setEditOpen(true)
  }

  const toggleProductStatus = async (product) => {
    await saveExistingProduct({
      ...cloneProduct(product),
      isActive: !product.isActive,
    })
  }

  const columns = [
    {
      key: 'name',
      header: 'Producto',
      render: (product) => (
        <div className="flex items-start gap-3">
          <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-100">
            {product.videos?.[0]?.thumbnailUrl ? (
              <img
                src={product.videos[0].thumbnailUrl}
                alt={product.name}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <Video className="h-5 w-5 text-gray-400" />
            )}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">{product.name}</div>
            <div className="mt-1 line-clamp-2 text-xs text-gray-500">
              {product.presentation || product.description || 'Sin descripcion registrada.'}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Categoria',
      render: (product) => (
        product.category?.name ? (
          <Badge variant="secondary">{product.category.name}</Badge>
        ) : (
          <span className="text-gray-400">Sin categoria</span>
        )
      ),
    },
    {
      key: 'slug',
      header: 'Slug',
      render: (product) => <span className="text-gray-600">{product.slug}</span>,
    },
    {
      key: 'price',
      header: 'Precio',
      render: (product) => (
        <span className="font-medium text-gray-700">
          {new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            maximumFractionDigits: 0,
          }).format(Number(product.price || 0))}
        </span>
      ),
    },
    {
      key: 'videos',
      header: 'Videos',
      render: (product) => (
        <span className="text-gray-600">{product.videos?.length || 0}</span>
      ),
    },
    {
      key: 'isActive',
      header: 'Estado',
      render: (product) => (
        <Button
          size="sm"
          variant={product.isActive ? 'accent' : 'secondary'}
          leftIcon={product.isActive ? <Power className="h-4 w-4" /> : <PowerOff className="h-4 w-4" />}
          onClick={() => toggleProductStatus(product)}
          disabled={saving}
        >
          {product.isActive ? 'Activo' : 'Inactivo'}
        </Button>
      ),
    },
  ]

  if (loading) {
    return (
      <PageContainer>
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <PageHeader
        title="Catalogo"
        subtitle="Listado de productos con tabla reusable y paginacion del servidor."
        actions={(
          <Button leftIcon={<Plus className="w-4 h-4" />} onClick={openCreateModal} disabled={!profile || categories.length === 0}>
            Nuevo producto
          </Button>
        )}
      />

      {message && (
        <div className={`mb-6 rounded-lg px-4 py-3 text-sm ${message.includes('correctamente') || message.includes('eliminado') || message.includes('guardado') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message}
        </div>
      )}

      <Card className="mb-6">
        <CardContent className="p-4 sm:p-6">
          <div className="grid grid-cols-1 items-end gap-4 lg:grid-cols-[1fr_auto]">
            <form className="grid gap-3 sm:grid-cols-[1fr_auto]" onSubmit={handleSearchSubmit}>
              <Input
                label="Buscar productos"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Buscar por nombre, slug o descripcion"
                leftIcon={<Search className="w-4 h-4" />}
              />
              <Button type="submit" className="self-end">
                Buscar
              </Button>
            </form>
            <div className="text-sm text-gray-500">
              {totalResults} resultado{totalResults === 1 ? '' : 's'}
            </div>
          </div>
        </CardContent>
      </Card>

      {!profile && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Primero configura el perfil del negocio en <Link to="/entrepreneurcatalog/configuration" className="font-medium underline">Perfil</Link> para poder crear productos.
        </div>
      )}

      {profile && categories.length === 0 && (
        <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          Antes de crear productos debes registrar al menos una <Link to="/entrepreneurcatalog/categories" className="font-medium underline">categoria</Link>.
        </div>
      )}

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-primary-600" />
              Productos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {products.length === 0 ? (
              <EmptyState
                icon={Video}
                title="Aun no hay productos"
                description="Usa el boton Nuevo producto para registrar el primero."
                size="sm"
                action={(
                  <Button leftIcon={<Plus className="w-4 h-4" />} onClick={openCreateModal} disabled={!profile || categories.length === 0}>
                    Nuevo producto
                  </Button>
                )}
              />
            ) : (
              <>
                <DataTable
                  columns={columns}
                  rows={products}
                  actions={(product) => (
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        leftIcon={<Eye className="h-4 w-4" />}
                        onClick={() => setViewProduct(product)}
                        disabled={saving}
                      >
                        Ver
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<Pencil className="h-4 w-4" />}
                        onClick={() => openEditModal(product)}
                        disabled={saving}
                      >
                        Editar
                      </Button>
                      <Button
                        size="sm"
                        variant="danger"
                        leftIcon={<Trash2 className="h-4 w-4" />}
                        onClick={() => setDeleteTarget(product)}
                        disabled={saving}
                      >
                        Eliminar
                      </Button>
                    </>
                  )}
                />

                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalCount}
                  pageSize={pagination.pageSize}
                  onPageChange={(page) => loadData(page, appliedQuery)}
                />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <ProductFormModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        mode="create"
        saving={saving}
        categories={categories}
        categoryOptions={categoryOptions}
        value={createModal}
        onProductChange={updateCreateProductField}
        onVideoChange={updateCreateVideoField}
        onSubmit={createProduct}
      />

      <ProductFormModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        mode="edit"
        saving={saving}
        categories={categories}
        categoryOptions={categoryOptions}
        value={editModal}
        onProductChange={updateEditProductField}
        onVideoChange={updateEditVideoField}
        onSubmit={saveEditProduct}
        onDeleteVideo={(videoId) => deleteVideo(editModal.product.id, videoId)}
      />

      <ProductViewModal
        isOpen={Boolean(viewProduct)}
        product={viewProduct}
        onClose={() => setViewProduct(null)}
      />

      <ConfirmModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={async () => {
          if (!deleteTarget) return
          await removeProduct(deleteTarget.id)
          setDeleteTarget(null)
        }}
        title="Eliminar producto"
        message={`Se eliminara el producto "${deleteTarget?.name || ''}". Esta accion no se puede deshacer.`}
        confirmText="Eliminar"
        loading={saving}
      />
    </PageContainer>
  )
}
